import { useTranslation } from 'react-i18next';
import { Button, PhotoGrid, Chip, EmptyState, StarRating, ListCard } from 'components/ds';
import styles from './VisitPhotos.module.css';

/**
 * 방문 사진 한 건 = 사진 여러 장 + 글 + 태그로 이루어진 게시물 하나.
 *
 * 참고 앱의 방문 사진과 같은 짜임입니다. 사진이 주인공이고 글은 딸립니다 —
 * 그래서 목록이 글이 아니라 사진 격자입니다. 글은 상세에서 읽습니다.
 * 별점만 모이 고유로 얹었습니다. 장소 목록의 외부 평점을 대신할 값이라서요.
 */
export interface VisitPhoto {
    postId: number;
    userNo: number | string;
    nickname: string;
    profileUrl?: string | null;
    /** 최대 5장 */
    photos: string[];
    content?: string | null;
    tags?: string[];
    rating?: number;
    createdAt: string;
    helpfulCount?: number;
    helpful?: boolean;
    /** 내가 쓴 글인가 — 더보기 메뉴가 갈립니다 */
    mine?: boolean;
    /** 어느 모임에서 나온 사진인지 */
    meetingName?: string | null;
}

const PREVIEW = 9;

export const VisitPhotoSection = ({
    posts, total, canAdd, onAdd, onMore, onOpen,
    tags, activeTag, onChangeTag,
}: {
    posts: VisitPhoto[];
    /** 전체 건수 — 미리보기만 받아도 제목에는 전체를 씁니다 */
    total?: number;
    canAdd?: boolean;
    onAdd?: () => void;
    /** 전체 목록으로 — 없으면 더보기 버튼을 숨깁니다 */
    onMore?: () => void;
    onOpen?: (post: VisitPhoto, index: number) => void;
    /** 태그 거르기 — 전체 목록에서만 씁니다 */
    tags?: string[];
    activeTag?: string;
    onChangeTag?: (tag: string) => void;
}) => {
    const { t } = useTranslation();
    const count = total ?? posts.length;
    const shown = posts.slice(0, PREVIEW);

    /* 별점만 남기고 간 후기는 사진이 없습니다. 격자에 넣으면 빈 칸이 되므로
       사진 있는 것만 격자에 두고, 글만 있는 후기는 아래에 줄로 보여줍니다.
       사진이 없다고 안 보여주면 별점만 남긴 사람의 이야기가 사라집니다. */
    const withPhotos = shown.filter(p => p.photos.length > 0);
    const textOnly = shown.filter(p => p.photos.length === 0);

    // 상세로 넘길 때는 언제나 원본 목록에서의 자리를 줍니다 — 좌우 이동이 어긋나지 않게
    const openAt = (post: VisitPhoto) => onOpen?.(post, posts.indexOf(post));

    return (
        <div className={styles.section}>
            <div className={styles.head}>
                <h2 className={styles.title}>
                    {t('visitPhoto.title')}
                    {/* 999 를 넘으면 정확한 수보다 많다는 사실이 중요합니다 */}
                    <span className={styles.count}>{count < 999 ? count : '999+'}</span>
                </h2>
                {canAdd && (
                    <Button size="sm" variant="secondary" onClick={onAdd}>
                        {t('visitPhoto.addCta')}
                    </Button>
                )}
            </div>

            {tags && tags.length > 0 && (
                <div className={styles.filter}>
                    <Chip
                        label={t('common.all')}
                        selected={!activeTag}
                        onSelect={() => onChangeTag?.('')}
                    />
                    {tags.map(tag => (
                        <Chip
                            key={tag}
                            label={`#${tag}`}
                            selected={activeTag === tag}
                            onSelect={() => onChangeTag?.(tag)}
                        />
                    ))}
                </div>
            )}

            {shown.length === 0 && <EmptyState title={t('visitPhoto.empty')} />}

            {withPhotos.length > 0 && (
                <PhotoGrid
                    photos={withPhotos.map(p => ({ id: String(p.postId), url: p.photos[0] }))}
                    onSelect={(_photo, i) => openAt(withPhotos[i])}
                />
            )}

            {textOnly.length > 0 && (
                <div className={styles.textOnly}>
                    {textOnly.map(p => (
                        <ListCard
                            key={p.postId}
                            title={p.nickname}
                            sub={p.content || undefined}
                            tags={p.tags}
                            aside={p.rating ? p.rating.toFixed(1) : undefined}
                            onClick={() => openAt(p)}
                        />
                    ))}
                </div>
            )}

            {onMore && count > shown.length && (
                <Button block variant="secondary" onClick={onMore}>
                    {t('visitPhoto.moreCta')}
                </Button>
            )}
        </div>
    );
};

/** 상세에서 쓰는 머리글 — 별점이 있으면 함께 보입니다 */
export const VisitPhotoByline = ({ post }: { post: VisitPhoto }) => {
    const { i18n } = useTranslation();
    return (
        <span className={styles.byline}>
            {new Date(post.createdAt).toLocaleDateString(i18n.language)}
            {post.meetingName ? ` · ${post.meetingName}` : ''}
            {post.rating ? <StarRating value={post.rating} size={12} /> : null}
        </span>
    );
};
